import React, { useEffect, useState } from 'react';
import { formatDateTime } from '@/utils/displayDateTime';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Form, Modal, Spinner } from '../../../components/ui';
import {
  COUNT_METHOD_OPTIONS,
  CountRequestError,
  materialCountService,
  type CountMethod,
  type CountSummary,
} from '../../../services/materialCountService';
import { countPath } from '../utils/siteDrillThrough';

interface StartCountModalProps {
  show: boolean;
  site: { id: string; name: string; number: string | null };
  /** Bins at the site holding a material: what the count will list. */
  binCount: number;
  onHide: () => void;
}

const pad = (n: number) => String(n).padStart(2, '0');
/** "2026-10-29T14:05" in the browser's time zone, for a datetime-local input. */
export function localDateTimeValue(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}


/**
 * Opens a count of every bin at a site, or goes back to the one already open. The count sheet
 * itself is a page (CountSessionPage): a whole site's bins do not fit a dialog.
 */
const StartCountModal: React.FC<StartCountModalProps> = ({ show, site, binCount, onHide }) => {
  const navigate = useNavigate();
  const [method, setMethod] = useState<CountMethod>('PHYSICAL');
  const [countedAt, setCountedAt] = useState('');
  const [note, setNote] = useState('');
  const [open, setOpen] = useState<CountSummary | null | 'loading'>('loading');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!show) return;
    setMethod('PHYSICAL');
    setCountedAt(localDateTimeValue(new Date()));
    setNote('');
    setError(null);
    setOpen('loading');
    materialCountService
      .list({ siteId: site.id, status: 'OPEN' })
      .then((counts) => setOpen(counts[0] ?? null))
      .catch(() => setOpen(null));
  }, [show, site.id]);

  const when = countedAt ? new Date(countedAt) : null;
  const problem = !when || Number.isNaN(when.getTime()) ? 'Enter when the count was taken.' : when.getTime() > Date.now() + 5 * 60_000 ? 'A count cannot be in the future.' : binCount === 0 ? 'No bin at this site holds a material yet.' : null;

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const count = await materialCountService.start({ siteId: site.id, method, countedAt: when!.toISOString(), note });
      navigate(countPath(count.id));
    } catch (err) {
      if (err instanceof CountRequestError && err.code === 'COUNT_ALREADY_OPEN' && err.countId) {
        navigate(countPath(err.countId));
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to start the count');
      setBusy(false);
    }
  };

  return (
    <Modal show={show} onHide={busy ? () => undefined : onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Count {site.number ? `${site.name} #${site.number}` : site.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="tw-space-y-4">
        {open === 'loading' ? (
          <div className="tw-flex tw-justify-center tw-py-6">
            <Spinner animation="border" size="sm" />
          </div>
        ) : open ? (
          <Alert variant="info" className="tw-mb-0">
            <p className="tw-mb-2">
              A {open.methodLabel.toLowerCase()} count is already open here, taken {formatDateTime(open.countedAt)}
              {open.createdBy ? ` by ${open.createdBy}` : ''}: {open.lines.counted} of {open.lines.total} bins counted, {open.lines.posted} posted.
            </p>
            <p className="tw-mb-0">Finish it, or close it, before starting another.</p>
          </Alert>
        ) : (
          <>
            <p className="tw-mb-0 tw-text-sm tw-text-content-secondary">
              Lists all {binCount} {binCount === 1 ? 'bin' : 'bins'} holding a material, each against what the ledger expected at
              the time of the count. Enter what was found; lines within tolerance post together, the rest wait for sign-off.
            </p>
            <fieldset>
              <legend className="tw-mb-2 tw-text-sm tw-font-medium tw-text-content">How was it counted?</legend>
              <div className="tw-grid tw-grid-cols-1 tw-gap-2 sm:tw-grid-cols-3">
                {COUNT_METHOD_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className={`tw-flex tw-cursor-pointer tw-flex-col tw-gap-0.5 tw-rounded-lg tw-border tw-px-3 tw-py-2 ${
                      method === option.value ? 'tw-border-primary tw-bg-primary/5' : 'tw-border-line hover:tw-bg-surface-hover'
                    }`}
                  >
                    <span className="tw-flex tw-items-center tw-gap-2 tw-font-medium tw-text-content">
                      <input type="radio" name="count-method" value={option.value} checked={method === option.value} onChange={() => setMethod(option.value)} />
                      {option.label}
                    </span>
                    <span className="tw-text-xs tw-text-content-muted">{option.hint}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <Form.Group controlId="count-counted-at">
              <Form.Label>When was it taken?</Form.Label>
              <Form.Control
                type="datetime-local"
                value={countedAt}
                max={localDateTimeValue(new Date())}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setCountedAt(event.target.value)}
              />
              <Form.Text>The count posts at this time, so anything recorded since still applies on top of it.</Form.Text>
            </Form.Group>
            <Form.Group controlId="count-note">
              <Form.Label>Note (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                maxLength={1000}
                value={note}
                placeholder="e.g. Survey report 2026-10-28, piles 1-4"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setNote(event.target.value)}
              />
            </Form.Group>
          </>
        )}
        {error ? <Alert variant="danger" className="tw-mb-0">{error}</Alert> : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={busy}>
          Cancel
        </Button>
        {open && open !== 'loading' ? (
          <Button variant="primary" onClick={() => navigate(countPath(open.id))}>
            Continue the open count
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={() => void start()}
            disabled={busy || open === 'loading' || problem !== null}
            disabledReason={busy || open === 'loading' ? undefined : problem ?? undefined}
          >
            {busy ? <Spinner animation="border" size="sm" /> : <i className="bi bi-clipboard-check" aria-hidden="true" />}
            Start the count
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default StartCountModal;
